import _defineProperty from "/app/node_modules/.pnpm/next@12.3.3_4afada721a4a39e35a1a66028f2134fd/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { camelCase } from 'lodash';
import { Button, Cell, Checkbox, InputText, Spacer, Text } from '@vinted/web-ui';
import useAbTest from 'hooks/useAbTest';
import useFormValidationMessage from 'hooks/useFormValidationMessage';
import useTranslate from 'hooks/useTranslate';
import { renderValidation } from 'components/Input';
import SeparatedList from 'components/SeparatedList';
import ContentLoader from 'components/ContentLoader';
import { BUSINESS_TERMS_URL, BUSINESS_TERMS_OF_SALE_URL, IMPRESSUM_URL, PRIVACY_POLICY_URL, TERMS_URL } from 'constants/routes';
import { navigateToPage } from 'libs/utils/window';
import { isValueInObject } from 'libs/utils/object';
import { getIsRealNameRequired, getNewsletterSubscription, getIsBusinessAuth } from 'state/auth/selectors';
import { NewsletterSubscription } from 'state/auth/constants';
import { validateUser } from 'data/api';
import { getNormalizedQueryParam } from 'libs/utils/url';
import { getGoogleRedirectUrl } from 'libs/utils/google';
import useDataDomeCaptcha from 'hooks/useDatadomeCaptcha';
import { validateEmail, validateLogin, validatePassword, validateRealName } from './utils';
import { Field } from './constants';
import PasswordField from '../PasswordField';
import { useEmailCodeTest } from '../hooks/useEmailCodeTest';
import useSuccessUrl from '../hooks/useSuccessUrl';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TRANSLATION_PREFIX = 'auth.register';
const BUSINESS_TRANSLATION_PREFIX = 'business';

const RegisterForm = _ref => {
  let {
    realName,
    email,
    isPasswordRequired,
    onSubmit
  } = _ref;
  const isRealNameRequired = useSelector(getIsRealNameRequired);
  const newsletterSubscription = useSelector(getNewsletterSubscription);
  const isBusinessAuth = useSelector(getIsBusinessAuth);
  const termsCheckboxABTest = useAbTest({
    abTestName: 'terms_checkbox',
    shouldTrackExpose: true
  });
  const {
    handleEmailCodeTest
  } = useEmailCodeTest();
  const showTermsAsCheckbox = (termsCheckboxABTest === null || termsCheckboxABTest === void 0 ? void 0 : termsCheckboxABTest.variant) === 'on';
  const isAutoSubscribed = newsletterSubscription === NewsletterSubscription.WithTerms;
  const {
    0: baseError,
    1: setBaseError
  } = useState('');
  const {
    0: showRealNameField,
    1: setShowRealNameField
  } = useState(false);
  const {
    0: isLoading,
    1: setIsLoading
  } = useState(false);
  const state = getNormalizedQueryParam('state');
  const stateUrl = getGoogleRedirectUrl(state);
  const successUrl = useSuccessUrl(stateUrl);
  const {
    setError,
    control,
    register,
    handleSubmit,
    getValues,
    formState: {
      isSubmitting,
      errors
    }
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      realName
    }
  });
  const getErrorMessage = useFormValidationMessage(errors, "".concat(TRANSLATION_PREFIX, ".fields"));
  const translate = useTranslate(TRANSLATION_PREFIX);
  const businessTranslate = useTranslate(BUSINESS_TRANSLATION_PREFIX);
  const setErrors = useCallback(newErrors => {
    let newBaseError = '';
    let hasFieldError = false;
    newErrors.forEach(_ref2 => {
      let {
        field,
        value
      } = _ref2;
      const fieldName = camelCase(field);
      if (fieldName === Field.RealName) setShowRealNameField(true);
      if (fieldName === 'base') newBaseError || (newBaseError = value);
      if (!isValueInObject(fieldName, Field)) return;
      hasFieldError = true;
      setError(fieldName, {
        type: 'manual',
        message: value
      });
    });
    setBaseError(hasFieldError ? '' : newBaseError);
  }, [setError]);

  async function handleFormSubmit(data) {
    const response = await onSubmit(data);
    if (!response) return;

    if ('errors' in response) {
      setErrors(response.errors);
      return;
    }

    const emailCodeTestUrl = await handleEmailCodeTest(response.user.id);
    navigateToPage(emailCodeTestUrl || successUrl);
  }

  useDataDomeCaptcha(() => {
    const formData = getValues();
    if (!formData) return;
    handleFormSubmit(formData);
  });

  function handleCheckboxClick(onChange) {
    return event => onChange(event.target.checked);
  }

  function renderBaseError() {
    if (!baseError) return null;
    return /*#__PURE__*/_jsx("div", {
      className: "u-ui-padding-horizontal-large u-ui-padding-top-large u-ui-padding-bottom-small",
      children: /*#__PURE__*/_jsx(Text, {
        text: baseError,
        theme: "warning",
        width: Text.Width.Parent,
        alignment: Text.Alignment.Center
      })
    });
  }

  function renderRealNameField() {
    if (!isRealNameRequired && !showRealNameField) return null;

    if (realName && !showRealNameField) {
      return /*#__PURE__*/_jsx("input", _objectSpread(_objectSpread({}, register(Field.RealName)), {}, {
        type: "hidden",
        value: realName
      }));
    }

    return /*#__PURE__*/_jsx(InputText, _objectSpread(_objectSpread({}, register(Field.RealName, {
      required: true,
      validate: value => value ? validateRealName(value) : undefined
    })), {}, {
      uncontrolled: true,
      placeholder: translate('fields.real_name.title'),
      validation: renderValidation(getErrorMessage(Field.RealName)) || translate('fields.real_name.hint')
    }));
  }

  function renderEmailField() {
    if (email) return /*#__PURE__*/_jsx("input", _objectSpread(_objectSpread({}, register(Field.Email)), {}, {
      type: "hidden",
      value: email
    }));
    return /*#__PURE__*/_jsx(InputText, _objectSpread(_objectSpread({}, register(Field.Email, {
      required: true,
      validate: validateEmail
    })), {}, {
      uncontrolled: true,
      placeholder: translate('fields.email.title'),
      validation: renderValidation(getErrorMessage(Field.Email))
    }));
  }

  function interpolateLink(url) {
    return part => /*#__PURE__*/_jsx("a", {
      href: url,
      target: "_blank",
      rel: "noopener noreferrer",
      children: part
    });
  }

  function translateTermsAndCondition(key) {
    return translate(key, {
      'terms-and-conditions': interpolateLink(TERMS_URL),
      'pro-terms-of-sale': interpolateLink(BUSINESS_TERMS_OF_SALE_URL),
      'privacy-policy': interpolateLink(PRIVACY_POLICY_URL),
      impressum: interpolateLink(IMPRESSUM_URL)
    });
  }

  function renderTermsAndConditionsCheckbox() {
    const error = getErrorMessage(Field.AgreeRules);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Controller, {
        defaultValue: false,
        render: _ref3 => {
          let {
            field: {
              name,
              value,
              onChange
            }
          } = _ref3;
          return /*#__PURE__*/_jsx(Checkbox, {
            name: name,
            text: translateTermsAndCondition('fields.agree_rules.title'),
            checked: value,
            onChange: handleCheckboxClick(onChange)
          });
        },
        rules: {
          required: true
        },
        name: Field.AgreeRules,
        control: control
      }), error && /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Small
      }), renderValidation(error)]
    });
  }

  function renderFields() {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderRealNameField(), /*#__PURE__*/_jsx(InputText, _objectSpread(_objectSpread({}, register(Field.Login, {
        required: true,
        minLength: 3,
        maxLength: 20,
        validate: validateLogin
      })), {}, {
        uncontrolled: true,
        placeholder: translate('fields.login.title'),
        validation: renderValidation(getErrorMessage(Field.Login)) || translate('fields.login.hint')
      })), renderEmailField(), isPasswordRequired && /*#__PURE__*/_jsx(PasswordField, _objectSpread(_objectSpread({}, register(Field.Password, {
        required: true,
        validate: value => value ? validatePassword(value) : undefined
      })), {}, {
        uncontrolled: true,
        validation: renderValidation(getErrorMessage(Field.Password)) || translate('fields.password.hint'),
        placeholder: translate('fields.password.title')
      })), (!isAutoSubscribed || showTermsAsCheckbox) && /*#__PURE__*/_jsx(Cell, {
        children: /*#__PURE__*/_jsxs(SeparatedList, {
          separator: /*#__PURE__*/_jsx(Spacer, {
            size: Spacer.Size.Large
          }),
          children: [/*#__PURE__*/_jsx(Controller, {
            defaultValue: newsletterSubscription === NewsletterSubscription.OptOut,
            render: _ref4 => {
              let {
                field: {
                  value,
                  name,
                  onChange
                }
              } = _ref4;
              return /*#__PURE__*/_jsx(Checkbox, {
                name: name,
                text: translate('fields.subscribe.title', {
                  // Only used for markets with a custom applied copy (e.g. Hungary)
                  'privacy-policy': interpolateLink(PRIVACY_POLICY_URL)
                }),
                checked: value,
                onChange: handleCheckboxClick(onChange)
              });
            },
            name: Field.SubscribeToNewsletter,
            control: control
          }), showTermsAsCheckbox && renderTermsAndConditionsCheckbox()]
        })
      })]
    });
  }

  const renderTermsAndConditions = () => {
    if (isBusinessAuth) {
      return /*#__PURE__*/_jsx(Text, {
        type: Text.Type.Caption,
        text: businessTranslate('account.signup.commercial_seller_terms', {
          'business-terms': interpolateLink(BUSINESS_TERMS_URL),
          'privacy-policy': interpolateLink(PRIVACY_POLICY_URL)
        })
      });
    }

    return /*#__PURE__*/_jsx(Text, {
      type: Text.Type.Caption,
      text: translateTermsAndCondition(isAutoSubscribed ? 'agree_terms_with_subscription' : 'agree_terms')
    });
  };

  function renderFooter() {
    return /*#__PURE__*/_jsxs(Cell, {
      children: [/*#__PURE__*/_jsx(Button, {
        text: translate('actions.submit'),
        type: Button.Type.Submit,
        styling: Button.Styling.Filled,
        disabled: isSubmitting,
        isLoading: isSubmitting
      }), /*#__PURE__*/_jsx(Spacer, {}), !showTermsAsCheckbox && /*#__PURE__*/_jsxs(_Fragment, {
        children: [renderTermsAndConditions(), /*#__PURE__*/_jsx(Spacer, {})]
      })]
    });
  }

  useEffect(() => {
    if (!isRealNameRequired) return;

    async function handleValidateInitialUserData() {
      setIsLoading(true);
      const response = await validateUser({
        user: {
          real_name: realName
        }
      });

      if ('errors' in response) {
        setErrors(response.errors);
      }

      setIsLoading(false);
    }

    handleValidateInitialUserData();
  }, [realName, setErrors, isRealNameRequired]);

  if (isLoading) {
    return /*#__PURE__*/_jsx(ContentLoader, {});
  }

  return /*#__PURE__*/_jsxs("form", {
    onSubmit: handleSubmit(handleFormSubmit),
    children: [renderBaseError(), renderFields(), renderFooter()]
  });
};

export default RegisterForm;